import React, { ForwardedRef, RefObject, memo } from 'react';

import { View } from 'react-native';

import { ASSET_CARD } from 'config/testID';

import {
    CastPerson,
    MixedItem,
    RBChannel,
    RBParsedRail,
    RBProgram,
    SearchProgram as SearchProgramType,
} from 'store/railsBuilder/reducer.types';
import { RecordingItem } from 'store/recordings/reducer.types';

import isCdvrRecordingItem from 'services/data-manager/parsing/utils/isCdvrRecordingItem';
import {
    CdvrRecording,
    ContentType,
    NowNextData,
} from 'services/data-manager/types';

import { OnUavAnalyticsProps, StandardUavProps } from 'components/Ribbon/types';

import CatchupRibbon from './Catchup';
import ChannelRibbon from './Channel';
import ContinueWatchingCdvrRecording from './ContinueWatchingCdvrRecording';
import EpisodeRibbon from './Episode';
import ProgramNowNextRibbon from './NowNext';
import PersonRibbon from './Person';
import RecordingRibbon from './Recording';
import SearchCatchupRibbon from './SearchCatchup';
import SearchProgram from './SearchProgram';
import SearchVod from './SearchVod';

type Props = {
    item: RBParsedRail['sections'][number];
    metadataType: ContentType;
    visibleItems: Record<string, boolean>;
    isCollection?: boolean;
    onPress?(item: RBParsedRail['sections'][number]): void;
    index?: number;
    onShowPinDialog?(): void;
    onSetParentalControlCheckedCallback?(
        callback: undefined | (() => void),
    ): void;
    totalNumberOfItems?: number;
    railTitle?: string;
} & OnUavAnalyticsProps &
    StandardUavProps;

const RibbonItem = (
    {
        item,
        isCollection = false,
        metadataType,
        onPress,
        onRailsSelection,
        onDeepLinkTriggered,
        visibleItems,
        railId,
        templateId,
        index,
        onShowPinDialog,
        onSetParentalControlCheckedCallback,
        totalNumberOfItems,
        railTitle,
    }: Props,
    forwardedRef?: ForwardedRef<View | undefined>,
) => {
    const testID = `${ASSET_CARD}_${railTitle}_${index}`;
    const ribbonItemRef = forwardedRef as RefObject<View> | undefined;

    switch (true) {
        case metadataType === ContentType.SearchCatchup:
            return (
                <SearchCatchupRibbon
                    ref={ribbonItemRef}
                    testID={testID}
                    item={item as SearchProgramType}
                    isCollection={isCollection}
                    railId={railId}
                    templateId={templateId}
                    onRailsSelection={onRailsSelection}
                    onShowPinDialog={onShowPinDialog}
                    onSetParentalControlCheckedCallback={
                        onSetParentalControlCheckedCallback
                    }
                    index={index}
                    totalNumberOfItems={totalNumberOfItems}
                />
            );
        case metadataType === ContentType.SearchProgram:
            return (
                <SearchProgram
                    ref={ribbonItemRef}
                    testID={testID}
                    item={item as SearchProgramType}
                    visibleItems={visibleItems}
                    isCollection={isCollection}
                    railId={railId}
                    templateId={templateId}
                    onRailsSelection={onRailsSelection}
                    onShowPinDialog={onShowPinDialog}
                    onSetParentalControlCheckedCallback={
                        onSetParentalControlCheckedCallback
                    }
                    index={index}
                    totalNumberOfItems={totalNumberOfItems}
                />
            );
        case metadataType === ContentType.SearchVod:
            return (
                <SearchVod
                    ref={ribbonItemRef}
                    testID={testID}
                    item={item as SearchProgramType}
                    isCollection={isCollection}
                    railId={railId}
                    templateId={templateId}
                    onRailsSelection={onRailsSelection}
                    onShowPinDialog={onShowPinDialog}
                    onSetParentalControlCheckedCallback={
                        onSetParentalControlCheckedCallback
                    }
                    index={index}
                    totalNumberOfItems={totalNumberOfItems}
                />
            );
        case metadataType === ContentType.NowNext:
            return (
                <ProgramNowNextRibbon
                    ref={ribbonItemRef}
                    item={item as NowNextData}
                    visibleItems={visibleItems}
                    isCollection={isCollection}
                    onRailsSelection={onRailsSelection}
                    onDeepLinkTriggered={onDeepLinkTriggered}
                    railId={railId}
                    templateId={templateId}
                    testID={testID}
                    onShowPinDialog={onShowPinDialog}
                    onSetParentalControlCheckedCallback={
                        onSetParentalControlCheckedCallback
                    }
                    index={index}
                    totalNumberOfItems={totalNumberOfItems}
                />
            );
        case metadataType === ContentType.ChannelItem:
            return (
                <ChannelRibbon
                    ref={ribbonItemRef}
                    item={item as RBChannel}
                    isCollection={isCollection}
                    onRailsSelection={onRailsSelection}
                    onDeepLinkTriggered={onDeepLinkTriggered}
                    railId={railId}
                    templateId={templateId}
                    testID={testID}
                    index={index}
                    totalNumberOfItems={totalNumberOfItems}
                />
            );
        case metadataType === ContentType.Catchup:
        case metadataType === ContentType.VOD:
        case metadataType === ContentType.Mixed:
            return (
                <CatchupRibbon
                    ref={ribbonItemRef}
                    testID={testID}
                    item={item as MixedItem}
                    isCollection={isCollection}
                    railId={railId}
                    templateId={templateId}
                    onRailsSelection={onRailsSelection}
                    onDeepLinkTriggered={onDeepLinkTriggered}
                    onShowPinDialog={onShowPinDialog}
                    onSetParentalControlCheckedCallback={
                        onSetParentalControlCheckedCallback
                    }
                    index={index}
                    totalNumberOfItems={totalNumberOfItems}
                />
            );
        case metadataType === ContentType.ContinueWatching:
            if (isCdvrRecordingItem(item)) {
                return (
                    <ContinueWatchingCdvrRecording
                        ref={ribbonItemRef}
                        testID={testID}
                        index={index}
                        item={item as CdvrRecording}
                        isCollection={isCollection}
                        railId={railId}
                        templateId={templateId}
                        totalNumberOfItems={totalNumberOfItems}
                        onRailsSelection={onRailsSelection}
                        onDeepLinkTriggered={onDeepLinkTriggered}
                        onShowPinDialog={onShowPinDialog}
                        onSetParentalControlCheckedCallback={
                            onSetParentalControlCheckedCallback
                        }
                    />
                );
            }

            return (
                <CatchupRibbon
                    ref={ribbonItemRef}
                    testID={testID}
                    assetContentType={metadataType}
                    item={item as MixedItem}
                    isCollection={isCollection}
                    railId={railId}
                    templateId={templateId}
                    totalNumberOfItems={totalNumberOfItems}
                    onRailsSelection={onRailsSelection}
                    onDeepLinkTriggered={onDeepLinkTriggered}
                    onShowPinDialog={onShowPinDialog}
                    onSetParentalControlCheckedCallback={
                        onSetParentalControlCheckedCallback
                    }
                />
            );
        case metadataType === ContentType.CastPerson:
            return (
                <PersonRibbon
                    ref={ribbonItemRef}
                    testID={testID}
                    item={item as CastPerson}
                />
            );
        case metadataType === ContentType.DetailsEpisode:
            return (
                <EpisodeRibbon
                    ref={ribbonItemRef}
                    testID={testID}
                    item={item as RBProgram | RecordingItem}
                    isCollection={isCollection}
                    visibleItems={visibleItems}
                    onPress={onPress}
                    railId={railId}
                    templateId={templateId}
                    index={index}
                    totalNumberOfItems={totalNumberOfItems}
                />
            );
        case metadataType === ContentType.NPVR:
            return (
                <RecordingRibbon
                    ref={ribbonItemRef}
                    testID={testID}
                    item={item as RecordingItem}
                    isCollection={isCollection}
                    railId={railId}
                    templateId={templateId}
                    onRailsSelection={onRailsSelection}
                    index={index}
                    totalNumberOfItems={totalNumberOfItems}
                />
            );
        default:
            return <></>;
    }
};

export default memo(React.forwardRef(RibbonItem));
